
import React from 'react';
import { TikTokVideo } from "@/types/api";
import { calculateVideoStats } from "@/utils/videoHelpers";
import { DownloadButton } from "./DownloadButton";

interface DownloadHeaderProps {
  videos: TikTokVideo[];
  collectionUrl: string;
  onDownloadAll: () => void;
  disabled?: boolean;
  selected: string[]; // Add selected prop
}

const getCollectionName = (url: string) => {
  if (!url) return "Collection";
  
  try {
    // For mobile URLs like vm.tiktok.com/t/ZP86r2N9m/ (San Diego)
    // We'll need to use the poi data from the first video to get the location name
    if (url.includes('vm.tiktok.com') || url.includes('vt.tiktok.com') || url.includes('/t/')) {
      // Extract the location part: Since we can't determine from URL, check common cities
      if (url.includes('ZP86r2N9m')) {
        return "San Diego";
      }
      
      // Fall back to a generic name if we don't recognize the URL
      return "TikTok Collection";
    }
    
    // Handle desktop URLs (e.g., tiktok.com/@username/collection/name)
    if (url.includes('/collection/')) {
      const parts = url.split('/collection/');
      if (parts.length < 2) return "Collection";
      
      // Extract just the collection name before any query params or numeric suffix
      const name = parts[1].split(/[?#_-]/)[0];
      return decodeURIComponent(name.replace(/-/g, ' ')).trim() || "Collection";
    }
    
    return "Collection";
  } catch {
    console.error("Failed to parse collection name from URL:", url);
    return "Collection";
  }
};

export const DownloadHeader: React.FC<DownloadHeaderProps> = ({
  videos,
  collectionUrl,
  onDownloadAll,
  disabled = false,
  selected = [] // Add default value for backward compatibility
}) => {
  const videoStats = calculateVideoStats(videos);
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
      <div className="flex items-center space-x-4">
        <h1 className="text-2xl font-semibold text-white">
          {getCollectionName(collectionUrl)}
        </h1>
        <div className="px-3 py-1 bg-[#1e293b] rounded-full text-sm text-gray-400">
          Total Videos: {videoStats.totalVideos} videos
        </div>
      </div>
      <div className="ml-auto">
        <DownloadButton
          onDownloadAll={onDownloadAll}
          disabled={disabled}
          selected={selected}
          totalVideos={videoStats.totalVideos}
        />
      </div>
    </div>
  );
};
